import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { waHref } from "@/lib/attribution";
import { useCopy } from "@/lib/i18n/copy";
import { WHATSAPP } from "@/content/chrome";

/**
 * Site-wide floating WhatsApp button (BTQ-65/66 wave 2, 2026-08-06).
 *
 * Bottom-LEFT deliberately: bottom-right is taken by StickyCTA. It MIRRORS that
 * button — same 48px size, same bottom offset and the same scroll gate (>500px)
 * — so the pair sits on one line and fades in and out together. Hidden on
 * /contact (every channel is already on show there) and in the /portal area.
 *
 * The href is built at render time through waHref(), so the prefilled text ends
 * with the visitor's [ref:EB-XXXXX] code. Because that code is per-visitor, the
 * button renders client-side only (mounted gate) and must never appear in
 * build-time prerendered HTML. contact_click{method:"whatsapp"} is fired by the
 * delegated listener in lib/analytics.ts; data-analytics-location declares
 * sticky_global per spec 06.08 §3.
 *
 * offsetBottom mirrors StickyCTA's convention: Layout passes the cookie-banner
 * visibility flag and the button climbs above the banner bar while it is open.
 */


const StickyWhatsApp = ({ offsetBottom = false }: { offsetBottom?: boolean }) => {
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  /* Same gate as StickyCTA so the pair appears and hides in step. */
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setVisible(window.scrollY > 500);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const path =
    location.pathname.length > 1 ? location.pathname.replace(/\/+$/, "") : location.pathname;

  const { t } = useCopy(WHATSAPP);

  if (!mounted) return null;
  if (path === "/contact" || path.startsWith("/portal")) return null;

  return (
    <a
      href={waHref(t.prefill)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t.ariaLabel}
      data-analytics-location="sticky_global"
      className="fixed left-6 z-50 flex h-12 w-12 items-center justify-center rounded-full transition-all duration-300 ease-in-out hover:scale-[1.08]"
      style={{
        bottom: offsetBottom ? 80 : 24,
        backgroundColor: "#25D366",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-[22px] w-[22px]"
        fill="#FFFFFF"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
};

export default StickyWhatsApp;
